import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CheckCircle2,
  ImageIcon,
  ScanFace,
  Sparkles,
  Wand2
} from 'lucide-react';
import Logo from '../../assets/logo_klikin.png';

const STEP_LOGS = [
  [
    'Reading dream preferences...',
    'Analyzing selected future path...',
    'Building imagination profile...'
  ],

  [
    'Scanning facial structure...',
    'Preserving identity features...',
    'Matching visual characteristics...'
  ],

  [
    'Designing editorial composition...',
    'Generating typography layout...',
    'Creating visual hierarchy...'
  ],

  [
    'Rendering dream artwork...',
    'Applying cinematic lighting...',
    'Generating premium cover...'
  ],

  [
    'Upscaling final image...',
    'Optimizing visual quality...',
    'Preparing final result...'
  ]
];

const DREAM_MAP = {
  doctor: {
    role: 'Doctor',
    headlines: [
      'Future Healer',
      'Saving Lives',
      'Healing Tomorrow',
      'Care Beyond Limits',
      'Medical Visionary'
    ],
    format:
      'medical excellence editorial poster, modern hospital environment, professional healthcare leader'
  },

  pilot: {
    role: 'Pilot',
    headlines: [
      'Sky Navigator',
      'Above The Clouds',
      'Born To Fly',
      'Limitless Horizons',
      'Captain Of Dreams'
    ],
    format:
      'aviation editorial poster, commercial aircraft setting, cinematic sky'
  },

  police_officer: {
    role: 'Police Officer',
    headlines: [
      'Community Guardian',
      'Serving With Honor',
      'Protect And Inspire',
      'City Protector',
      'Courage In Action'
    ],
    format:
      'public service editorial poster, modern city environment, trustworthy presence'
  },

  musician: {
    role: 'Musician',
    headlines: [
      'Creative Rhythm',
      'Sound Of Dreams',
      'Born To Perform',
      'Melody Maker',
      'The Next Encore'
    ],
    format:
      'music performance editorial poster, concert stage atmosphere, cinematic lighting'
  },

  athlete: {
    role: 'Professional Athlete',
    headlines: [
      'Champion Mindset',
      'Built To Win',
      'Beyond Limits',
      'Game Changer',
      'Rise To Greatness'
    ],
    format:
      'sports editorial poster, championship atmosphere, elite performance energy'
  },

  teacher: {
    role: 'Teacher',
    headlines: [
      'Inspiring Minds',
      'Shaping Futures',
      'Teaching Tomorrow',
      'Knowledge Ignited',
      'Guiding Greatness'
    ],
    format:
      'education leadership editorial poster, empowering learning environment'
  },

  scientist: {
    role: 'Scientist',
    headlines: [
      'Future Discovery',
      'Driven By Curiosity',
      'Beyond Knowledge',
      'Innovation Starts Here',
      'Exploring Tomorrow'
    ],
    format:
      'scientific innovation editorial poster, advanced research environment'
  },

  chef: {
    role: 'Chef',
    headlines: [
      'Culinary Vision',
      'Taste The Future',
      'Flavor Creator',
      'Art Of Taste',
      'Inspired By Passion'
    ],
    format: 'culinary excellence editorial poster, premium kitchen atmosphere'
  },

  architect: {
    role: 'Architect',
    headlines: [
      'Design Tomorrow',
      'Building Dreams',
      'Vision Into Reality',
      'Future By Design',
      'Inspired Structures'
    ],
    format: 'architectural vision editorial poster, iconic building concepts'
  },

  entrepreneur: {
    role: 'Entrepreneur',
    headlines: [
      'Dream Builder',
      'Creating Impact',
      'Vision To Reality',
      'Built For Success',
      'Future Founder'
    ],
    format:
      'business leadership editorial poster, innovative workspace, visionary founder mindset'
  }
};

const STEPS = [
  {
    title: 'Reading Your Dream',
    icon: Sparkles
  },

  {
    title: 'Analyzing Your Photo',
    icon: ScanFace
  },

  {
    title: 'Designing Artwork',
    icon: ImageIcon
  },

  {
    title: 'Creating Dream Cover',
    icon: Wand2
  },

  {
    title: 'Finalizing',
    icon: CheckCircle2
  }
];

export default function PageGenerate() {
  //const dickyAPI = 'wsk_live_9VghgyFaP1ltLLMiDfbQa6FUz0icobdq6bp0_acSXgg'; //dickyjayaumbara.game@gmail.com
  const dickyAPI = 'wsk_live_kF0sZ8PQIc_eaZrX-Fubbvs0bRrADW4mSlepi1Y4zgI'; //email midun

  const navigate = useNavigate();

  const [progress, setProgress] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const currentLogs = STEP_LOGS[activeStep] || [];

  const photoboothData = useMemo(() => {
    return JSON.parse(localStorage.getItem('photobooth_data')) || {};
  }, []);

  const dream = photoboothData?.answers?.dream_life;

  const inspiration = photoboothData?.answers?.inspiration;

  const world = photoboothData?.answers?.dream_world;

  const toBase64 = async (url) => {
    const response = await fetch(url);

    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(blob);

      reader.onloadend = () => {
        resolve(reader.result);
      };

      reader.onerror = reject;
    });
  };

  const generateAI = useCallback(async () => {
    try {
      const userPhoto = photoboothData?.captured_photo;

      if (!userPhoto) {
        alert('Photo tidak ditemukan');
        return;
      }

      const userBase64 = await toBase64(userPhoto);

      const logoBase64 = await toBase64(Logo);

      const prompt = buildPrompt(photoboothData.answers);

      const response = await fetch(
        'https://api.wavespeed.ai/api/v3/wavespeed-ai/flux-2-klein-9b/edit',
        //  'https://api.wavespeed.ai/api/v3/pruna-ai/p-image/edit',
        {
          method: 'POST',

          headers: {
            'Content-Type': 'application/json',

            Authorization: `Bearer ${dickyAPI}`
          },

          body: JSON.stringify({
            enable_base64_output: false,

            enable_image_search: false,

            enable_sync_mode: true,

            enable_web_search: false,

            seed: 2063964724,
            size: '720*1280',
            // images: [
            //   "https://d1q70p.cachecloud.net/media/f6d28b53d2cf4d57896ec695a3fc5d51/images/1780158281689096268_o4fvFPY7.jpg"
            // ],
            prompt,
            images: [userBase64]
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.message || 'AI Generation Failed');
      }

      const outputUrl = result?.data?.outputs?.[0];

      if (!outputUrl) {
        throw new Error('Output image not found');
      }

      const updatedData = {
        ...photoboothData,
        generated_photo: outputUrl
      };

      localStorage.setItem('photobooth_data', JSON.stringify(updatedData));

      await new Promise((r) => setTimeout(r, 1000));

      await setProgress(100);

      setTimeout(() => {
        navigate('/result');
      }, 1000);
    } catch (err) {
      console.error(err);

      alert(err?.message || 'Failed generating AI photo');

      navigate('/');
    }
  }, [navigate, photoboothData]);

  const startFakeSteps = async () => {
    // =========================
    // STEP 1
    // =========================
    setActiveStep(0);
    await smoothProgress(0, 18, 1000);

    await new Promise((r) => setTimeout(r, 1000));

    // =========================
    // STEP 2
    // =========================
    setActiveStep(1);
    await smoothProgress(18, 38, 1000);

    await new Promise((r) => setTimeout(r, 1000));

    // =========================
    // STEP 3
    // =========================
    setActiveStep(2);
    await smoothProgress(28, 58, 1000);

    await new Promise((r) => setTimeout(r, 1000));

    // =========================
    // STEP 4
    // =========================
    setActiveStep(3);
    await smoothProgress(38, 88, 1000);
  };

  useEffect(() => {
    generateAI();
    startFakeSteps();
  }, [generateAI]);

  const smoothProgress = (from, to, duration = 1000) => {
    return new Promise((resolve) => {
      const start = performance.now();

      const animate = (now) => {
        const progress = Math.min((now - start) / duration, 1);

        const value = from + (to - from) * progress;

        setProgress(value);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  };

  const inspirationPromptMap = {
    creativity: 'creative, imaginative, expressive, artistic energy',
    innovation: 'forward-thinking, inventive, visionary mindset',
    exploration: 'curious, adventurous, discovery-driven spirit',
    achievement: 'confident, ambitious, goal-oriented presence',
    leadership: 'inspiring, influential, empowering character',
    learning: 'intelligent, thoughtful, lifelong learner',
    discovery: 'curious researcher, innovative thinker',
    music: 'expressive, emotional, artistic personality',
    nature: 'grounded, calm, harmonious presence',
    possibility: 'optimistic, future-focused, dream-driven mindset'
  };

  const selectedInspiration =
    inspirationPromptMap[inspiration?.value] ||
    'inspiring and ambitious personality';

  const worldPromptMap = {
    futuristic:
      'advanced future city, innovative architecture, cinematic future world',
    modern_city: 'modern metropolitan skyline, contemporary urban energy',
    cinematic: 'dramatic cinematic environment, film-inspired atmosphere',
    nature: 'majestic natural landscape, peaceful organic environment',
    tropical: 'vibrant tropical scenery, warm atmosphere',
    creative_studio: 'modern creative workspace filled with inspiration',
    mountain_escape: 'epic mountain landscape with adventurous spirit',
    ocean_horizon: 'expansive oceanfront environment with limitless horizons',
    space_frontier: 'inspiring space-age exploration setting',
    urban_lights: 'vibrant city lights and modern urban atmosphere'
  };

  const selectedWorld =
    worldPromptMap[world?.value] || 'inspiring cinematic environment';

  const buildPrompt = (answers) => {
    //const dream = answers?.dream_life;
    const inspiration = answers?.inspiration;
    const world = answers?.dream_world;

    const dreamData = DREAM_MAP[dream?.value] || {};

    return `
    The person or people from the reference image must remain instantly recognizable. Keep the exact same number of people, pose, camera angle, framing, and composition from the original photo. Preserve identity, facial features, hairstyle, hairline, facial proportions, skin tone, expression, and body proportions exactly. Do not create extra limbs, missing limbs, altered anatomy, or distorted facial features. Replace only the clothing with ${answers?.template?.value}. Photorealistic, cinematic photography, natural skin texture, sharp facial details, realistic lighting, shallow depth of field, premium editorial quality, highly detailed, Leica portrait photography, ultra realistic, vertical 9:16. The subjects remain the clear focus of the image. No text, no typography, no logos, no watermark.
    `;

    // return `
    // The person or people from the reference image remain completely unchanged and instantly recognizable.

    // Keep the exact same number of people shown in the reference image.

    // Keep the pose, angle, and composition of the original photo.

    // dont create any new anaotomical features that are not in the original photo, such as extra limbs, missing limbs, or altered facial features.

    // Maintain the identity, face, hairstyle, hairline, facial proportions, skin tone, expression, body proportions, and overall appearance of every individu

    // ${answers?.template?.value}

    // Photorealistic, cinematic photography, natural skin texture, sharp facial details, realistic lighting, shallow depth of field, premium editorial quality, highly detailed, Leica portrait photography, ultra realistic, vertical 9:16.

    // The subject or subjects remain the clear focus of the image.

    // No text, no typography, no logos, no watermark.

    // `;
  };

  return (
    <div className="relative h-full w-full overflow-hidden bg-[#0f1020] text-white">
      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,#8b5cf620,transparent_45%)]" />

      <div className="relative z-10 flex h-full flex-col px-6 py-8">
        {/* Header */}
        <div className="text-center">
          <p className="flex justify-center items-center text-xs font-bold tracking-[0.4em] text-violet-300">
            <img src={Logo} className="h-10 w-auto object-fit-contain" />
          </p>

          <h1 className="mt-5 text-[34px] font-black">Creating Your Dream</h1>

          <p className="mt-3 text-sm text-zinc-400">
            AI is imagining your dream version.
          </p>
        </div>

        {/* circle percent */}

        {/* Center */}
        {/* AI TERMINAL */}
        <div className="mt-10 w-full overflow-hidden rounded-3xl border border-violet-500/20 bg-black/50 backdrop-blur-xl">
          {/* Header */}
          <div className="flex items-center gap-2 border-b border-violet-500/10 px-4 py-3">
            <div className="h-2 w-2 rounded-full bg-red-500" />
            <div className="h-2 w-2 rounded-full bg-yellow-500" />
            <div className="h-2 w-2 rounded-full bg-green-500" />

            <p className="ml-2 text-[10px] font-mono uppercase tracking-[0.25em] text-violet-300">
              KLIKIN STEP INTO YOUR DREAM
            </p>
          </div>

          {/* Content */}
          <div className="space-y-3 p-4 font-mono">
            {STEPS.map((step, index) => {
              const isDone = activeStep > index;
              const isActive = activeStep === index;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-sm">
                  {isDone && (
                    <div className="flex items-center gap-3">
                      <span className="text-emerald-400">[OK]</span>

                      <span className="text-zinc-300">
                        {step.title.toUpperCase()}
                      </span>
                    </div>
                  )}

                  {isActive && (
                    <div className="flex items-center gap-3">
                      <motion.span
                        animate={{
                          opacity: [1, 0.4, 1]
                        }}
                        transition={{
                          repeat: Infinity,
                          duration: 1
                        }}
                        className="text-violet-400">
                        [RUN]
                      </motion.span>

                      <span className="text-violet-200">
                        {step.title.toUpperCase()}
                      </span>

                      <motion.span
                        animate={{
                          opacity: [1, 0, 1]
                        }}
                        transition={{
                          repeat: Infinity,
                          duration: 0.8
                        }}
                        className="text-violet-400">
                        ▌
                      </motion.span>
                    </div>
                  )}

                  {!isDone && !isActive && (
                    <div className="flex items-center gap-3">
                      <span className="text-zinc-600">[--]</span>

                      <span className="text-zinc-600">
                        {step.title.toUpperCase()}
                      </span>
                    </div>
                  )}
                </motion.div>
              );
            })}

            {/* Progress */}
            <div className="pt-5">
              <div className="mb-3 flex items-center justify-between">
                <span className="font-mono text-xs tracking-[0.25em] text-violet-300">
                  DREAM_PROGRESS
                </span>

                <motion.span
                  key={progress}
                  initial={{ scale: 1.2 }}
                  animate={{ scale: 1 }}
                  className="font-mono text-lg font-black text-white">
                  {Math.round(progress)}%
                </motion.span>
              </div>

              <div className="relative h-4 overflow-hidden rounded-full border border-violet-500/30 bg-black">
                {/* Background Grid */}
                <div
                  className="absolute inset-0 opacity-20"
                  style={{
                    backgroundImage:
                      'repeating-linear-gradient(90deg, transparent, transparent 8px, rgba(255,255,255,0.1) 8px, rgba(255,255,255,0.1) 10px)'
                  }}
                />

                {/* Fill */}
                <motion.div
                  animate={{
                    width: `${progress}%`
                  }}
                  transition={{
                    duration: 0.5
                  }}
                  className="relative h-full overflow-hidden bg-gradient-to-r from-violet-500 via-fuchsia-400 to-cyan-400">
                  {/* Moving Shine */}
                  <motion.div
                    animate={{
                      x: ['-100%', '250%']
                    }}
                    transition={{
                      repeat: Infinity,
                      duration: 1.5,
                      ease: 'linear'
                    }}
                    className="absolute top-0 h-full w-16 bg-white/40 blur-sm"
                  />
                </motion.div>

                {/* Glow */}
                <motion.div
                  animate={{
                    width: `${progress}%`
                  }}
                  transition={{
                    duration: 0.5
                  }}
                  className="absolute top-0 h-full bg-violet-500/30 blur-md"
                />
              </div>
            </div>

            {/* Live Log */}
            <motion.div
              key={activeStep}
              initial={{
                opacity: 0
              }}
              animate={{
                opacity: 1
              }}
              className="border-t border-violet-500/10 pt-4 text-xs text-violet-300">
              <p>&gt; AI STATUS LOG</p>

              <div className="mt-3 space-y-1">
                {currentLogs.map((log) => (
                  <motion.p
                    key={log}
                    initial={{
                      opacity: 0,
                      x: -10
                    }}
                    animate={{
                      opacity: 1,
                      x: 0
                    }}
                    className="text-zinc-400">
                    &gt; {log}
                  </motion.p>
                ))}
              </div>

              <div className="mt-2 flex items-center">
                <motion.span
                  animate={{
                    opacity: [1, 0, 1]
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 0.8
                  }}>
                  ▌
                </motion.span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
